/**
 * Session state machine — pure reducer.
 *
 * IMPORTANT: This file MUST NOT import any browser API. It is the Rust/WASM
 * migration target. All logic is expressed as:
 *   sessionTransition(state, event, attempts) → { next, effects, nextAttempts }
 *
 * The host runtime reads the returned effects and executes them against the
 * real browser APIs (WebSocket, RTCPeerConnection, DOM).
 */

export type SessionState =
  | 'idle'
  | 'opening'
  | 'transport_connecting'
  | 'playing'
  | 'buffering'
  | 'reconnecting'
  | 'failed'
  | 'stopped';

export type SessionEvent =
  | { type: 'OPEN' }
  | { type: 'BOOTSTRAP_RECEIVED'; hasInteractive: boolean; hasStreamSocket: boolean }
  | { type: 'TRANSPORT_CONNECTED'; family: 'interactive' | 'stream_socket' }
  | { type: 'TRANSPORT_FAILED'; reason: string; canRetry: boolean }
  | { type: 'MEDIA_FLOWING' }
  | { type: 'MEDIA_STALLED' }
  | { type: 'MEDIA_RESUMED' }
  | { type: 'CLOSE' }
  | { type: 'RECONNECT_TICK' };

export type SessionEffect =
  | { type: 'SEND_OPEN' }
  | { type: 'START_TRANSPORT'; family: 'interactive' | 'stream_socket' }
  | { type: 'STOP_TRANSPORT' }
  | { type: 'EMIT_STATE'; state: SessionState; message?: string }
  | { type: 'EMIT_TRANSPORT_SWITCHED'; family: 'interactive' | 'stream_socket' }
  | { type: 'SCHEDULE_RECONNECT'; delayMs: number };

const MAX_RECONNECT_ATTEMPTS = 3;
const RECONNECT_BASE_DELAY_MS = 1500;

export interface TransitionResult {
  next: SessionState;
  effects: SessionEffect[];
  nextAttempts: number;
}

export function sessionTransition(
  current: SessionState,
  event: SessionEvent,
  attempts: number,
): TransitionResult {
  switch (current) {
    case 'idle': {
      if (event.type === 'OPEN') {
        return {
          next: 'opening',
          effects: [{ type: 'SEND_OPEN' }, { type: 'EMIT_STATE', state: 'opening' }],
          nextAttempts: attempts,
        };
      }
      break;
    }

    case 'opening': {
      if (event.type === 'BOOTSTRAP_RECEIVED') {
        // Determine which transport the device actually supports.
        // P2P and stream_socket are equal-first options — whichever the device
        // advertises is used exclusively; they never race.
        if (event.hasInteractive) {
          return {
            next: 'transport_connecting',
            effects: [
              { type: 'START_TRANSPORT', family: 'interactive' },
              { type: 'EMIT_STATE', state: 'transport_connecting' },
            ],
            nextAttempts: attempts,
          };
        }
        if (event.hasStreamSocket) {
          return {
            next: 'transport_connecting',
            effects: [
              { type: 'START_TRANSPORT', family: 'stream_socket' },
              { type: 'EMIT_STATE', state: 'transport_connecting' },
            ],
            nextAttempts: attempts,
          };
        }
        return {
          next: 'failed',
          effects: [
            { type: 'EMIT_STATE', state: 'failed', message: 'no_compatible_transport' },
          ],
          nextAttempts: attempts,
        };
      }
      if (event.type === 'CLOSE') return closedResult();
      break;
    }

    case 'transport_connecting': {
      if (event.type === 'TRANSPORT_CONNECTED') {
        return {
          next: 'playing',
          effects: [
            { type: 'EMIT_TRANSPORT_SWITCHED', family: event.family },
            { type: 'EMIT_STATE', state: 'playing' },
            { type: 'START_TRANSPORT', family: event.family },
          ],
          nextAttempts: 0,
        };
      }
      if (event.type === 'TRANSPORT_FAILED') {
        return handleFailure(attempts, event.reason, event.canRetry);
      }
      if (event.type === 'CLOSE') return closedResult();
      break;
    }

    case 'playing': {
      if (event.type === 'MEDIA_STALLED') {
        return {
          next: 'buffering',
          effects: [{ type: 'EMIT_STATE', state: 'buffering' }],
          nextAttempts: attempts,
        };
      }
      if (event.type === 'TRANSPORT_FAILED') {
        return handleFailure(attempts, event.reason, event.canRetry);
      }
      if (event.type === 'CLOSE') return closedResult();
      break;
    }

    case 'buffering': {
      if (event.type === 'MEDIA_RESUMED' || event.type === 'MEDIA_FLOWING') {
        return {
          next: 'playing',
          effects: [{ type: 'EMIT_STATE', state: 'playing' }],
          nextAttempts: attempts,
        };
      }
      if (event.type === 'TRANSPORT_FAILED') {
        return handleFailure(attempts, event.reason, event.canRetry);
      }
      if (event.type === 'CLOSE') return closedResult();
      break;
    }

    case 'reconnecting': {
      if (event.type === 'RECONNECT_TICK') {
        return {
          next: 'opening',
          effects: [{ type: 'SEND_OPEN' }, { type: 'EMIT_STATE', state: 'opening' }],
          nextAttempts: attempts,
        };
      }
      if (event.type === 'CLOSE') return closedResult();
      break;
    }

    case 'failed':
    case 'stopped':
      break;
  }

  // Unhandled event in this state — no transition
  return { next: current, effects: [], nextAttempts: attempts };
}

function handleFailure(attempts: number, reason: string, canRetry: boolean = true): TransitionResult {
  const next = attempts + 1;
  if (!canRetry || next >= MAX_RECONNECT_ATTEMPTS) {
    return {
      next: 'failed',
      effects: [
        { type: 'STOP_TRANSPORT' },
        { type: 'EMIT_STATE', state: 'failed', message: reason },
      ],
      nextAttempts: next,
    };
  }
  const delayMs = RECONNECT_BASE_DELAY_MS * Math.pow(2, attempts);
  return {
    next: 'reconnecting',
    effects: [
      { type: 'STOP_TRANSPORT' },
      { type: 'EMIT_STATE', state: 'reconnecting', message: reason },
      { type: 'SCHEDULE_RECONNECT', delayMs },
    ],
    nextAttempts: next,
  };
}

function closedResult(): TransitionResult {
  return {
    next: 'stopped',
    effects: [
      { type: 'STOP_TRANSPORT' },
      { type: 'EMIT_STATE', state: 'stopped' },
    ],
    nextAttempts: 0,
  };
}
